import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { fetchPendingExamSessions, deleteExamSession } from "../utils/api";
import "./PendingExams.css";

const PendingExams = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [pendingExams, setPendingExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedExamForDelete, setSelectedExamForDelete] = useState(null);
  const [selectedCareHome, setSelectedCareHome] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !token || !["manager", "group_manager"].includes(user.role)) {
        console.warn("🔒 Access Denied: User is not authorized.");
        setError("Access denied.");
        setLoading(false);
        return;
      }

      try {
        const data = await fetchPendingExamSessions(token);
        const validData = Array.isArray(data) ? data : [];
        setPendingExams(validData);
        if (user.role === "manager") {
          const assignedHome = user.carehome?.toLowerCase();
          console.log("🔍 Manager assigned_home:", assignedHome);

          const filtered = validData.filter((exam) => {
            const careHome = (exam.care_home || exam.assigned_home || "").toLowerCase();
            return careHome === assignedHome;
          });

          setFilteredExams(filtered);
        } else {
          setFilteredExams(validData);
        }
      } catch (err) {
        console.error("❌ Failed to load pending exams:", err.message || err);
        setError("Failed to load pending exams.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, token]);

  useEffect(() => {
    if (user?.role === "group_manager") {
      if (selectedCareHome) {
        const filtered = pendingExams.filter(
          (exam) =>
            (exam.care_home || exam.assigned_home || "").toLowerCase() ===
            selectedCareHome.toLowerCase()
        );
        setFilteredExams(filtered);
      } else {
        setFilteredExams(pendingExams);
      }
    }
  }, [selectedCareHome, pendingExams, user]);

  const handleDelete = async () => {
    if (!selectedExamForDelete) return;
    try {
      await deleteExamSession(selectedExamForDelete.id, token);
      setPendingExams((prev) =>
        prev.filter((exam) => exam.id !== selectedExamForDelete.id)
      );
      setFilteredExams((prev) =>
        prev.filter((exam) => exam.id !== selectedExamForDelete.id)
      );
      setSelectedExamForDelete(null);
    } catch (err) {
      console.error("Failed to delete session:", err);
      alert("An error occurred while deleting the session.");
    }
  };

  const careHomes = [
    ...new Set(
      pendingExams.map((exam) =>
        (exam.care_home || exam.assigned_home || "").trim()
      )
    ),
  ].sort();

  if (loading) return <div className="loading">Loading pending exams...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="pending-exams-container">
      <h2>Employee Pending Exams To Start</h2>

      {user.role === "group_manager" && (
        <div className="filter-container">
          <label htmlFor="care-home-select">Filter by Care Home:</label>
          <select
            id="care-home-select"
            value={selectedCareHome}
            onChange={(e) => setSelectedCareHome(e.target.value)}
          >
            <option value="">All</option>
            {careHomes.map((home, index) => (
              <option key={index} value={home}>
                {home}
              </option>
            ))}
          </select>
        </div>
      )}

      {filteredExams.length === 0 ? (
        <p>No pending exams found.</p>
      ) : (
        <>
          <table className="pending-exams-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Care Home</th>
                <th>Exam Name</th>
                <th>Courses</th>
                <th>Date Created</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {filteredExams.map((exam) => (
                <tr key={exam.id}>
                  <td>{exam.employee_name || exam.user_name || "—"}</td>
                  <td>{exam.care_home || exam.assigned_home || "—"}</td>
                  <td>{exam.exam_name || exam.exam_title || "—"}</td>
                  <td>
                    {Array.isArray(exam.selected_courses)
                      ? exam.selected_courses.join(", ")
                      : exam.selected_courses || "—"}
                  </td>
                  <td>
                    {exam.created_at
                      ? new Date(exam.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => setSelectedExamForDelete(exam)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedExamForDelete && (
            <ConfirmDeleteModal
              exam={selectedExamForDelete}
              onCancel={() => setSelectedExamForDelete(null)}
              onConfirm={handleDelete}
            />
          )}
        </>
      )}
    </div>
  );
};

const ConfirmDeleteModal = ({ exam, onCancel, onConfirm }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>⚠️ Confirm Delete</h3>
        <p>
          Are you sure you want to delete the exam{" "}
          <strong>{exam.exam_name || exam.exam_title}</strong> for{" "}
          <strong>{exam.employee_name || exam.user_name}</strong>?
          <br />
          This <strong>cannot be undone</strong>.
        </p>
        <div className="modal-buttons">
          <button className="confirm-delete-btn" onClick={onConfirm}>
            DELETE
          </button>
          <button className="cancel-delete-btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingExams;
